import { CircularProgress, Dialog, Icon } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useState } from "react";

import { postActivateStripeSub, postCancelStripeSub } from "../../shared/api/request";
import "../../shared/css/ui.css";
import "../css/User.css";
import CheckoutForm from "./CheckoutFormStripe";

const stripePromise = loadStripe(
  // "pk_test_NUMrimezt2vKUknYbRFbjcm700fh5hmNeT"
  "pk_live_51EOM36EwDroA4LL2H3Mi5Kz2zmaYMcrSJHDkdQ6rhvZTyx7ItiXA61yNjtVltWuz2mXk7UBEuPjLK307UMfxeX6B00kouODdXQ"
);

export type Receipt = {
  // "other": subscribed through a partner (médiathèque, offre partenaire, code d'activation...)
  receiptType?: "none" | "in-app" | "stripe" | "gift" | "other";
  aboType?: "mensuel" | "annuel";
  // Stripe subscription status (active, trialing, past_due...)
  status?: string;
  // DD/MM/YYYY: renewal date, or end of access when the subscription is cancelled
  endAt?: string;
  // The subscription won't renew: access ends at endAt
  cancelAtPeriodEnd?: boolean;
  canceledAt?: string;
  duration?: number;
  timeUnit?: "m" | "w";
};

type Props = {
  receipt: Receipt;
  clientSecret: string;
  // Reloads the account infos (new subscription state, new Stripe setup intent...)
  onChanged: () => Promise<void>;
};

type Feedback = { type: "success" | "error"; message: string } | null;

// endAt comes as DD/MM/YYYY
const getDaysLeft = (date?: string) => {
  const [day, month, year] = (date || "").split("/").map(Number);
  if (!day || !month || !year) return null;
  const end = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((end.getTime() - today.getTime()) / 86400000));
};

const formatDaysLeft = (days: number | null) => {
  if (days === null) return null;
  if (days === 0) return "Aujourd'hui";
  if (days === 1) return "Demain";
  return `Dans ${days} jours`;
};

const KeyDate = ({ label, date, tone }: { label: string; date?: string; tone?: "warning" }) => {
  if (!date) return null;
  const daysLeft = formatDaysLeft(getDaysLeft(date));
  return (
    <div className={`pf-keydate${tone ? ` pf-keydate--${tone}` : ""}`}>
      <Icon aria-hidden="true">{tone === "warning" ? "event_busy" : "event_repeat"}</Icon>
      <div>
        <span className="pf-keydate__label">{label}</span>
        <strong className="pf-keydate__date">{date}</strong>
      </div>
      {daysLeft ? <span className="pf-keydate__countdown">{daysLeft}</span> : null}
    </div>
  );
};

const dialogPaperSx = {
  backgroundColor: "#2B1A59",
  backgroundImage: "none",
  color: "white",
  borderRadius: "20px",
  margin: "16px",
  width: "calc(100% - 32px)",
};

const SubscriptionCard = ({ receipt, clientSecret, onChanged }: Props) => {
  const [showCardForm, setShowCardForm] = useState(false);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const plan = receipt.aboType ? `Abonnement ${receipt.aboType}` : "Abonnement";

  const runAction = async (action: () => Promise<any>, successMessage: string) => {
    setIsLoading(true);
    setFeedback(null);
    const res = await action();
    if (res === "OK") {
      await onChanged();
      setFeedback({ type: "success", message: successMessage });
    } else {
      setFeedback({
        type: "error",
        message: "L'opération n'a pas pu aboutir. Réessayez ou contactez-nous à hello@nabook.co.",
      });
    }
    setIsLoading(false);
  };

  const cancelSubscription = async () => {
    await runAction(
      postCancelStripeSub,
      "Votre résiliation est bien enregistrée. Aucun nouveau prélèvement ne sera effectué."
    );
    setIsConfirmingCancel(false);
  };

  const reactivateSubscription = () =>
    runAction(postActivateStripeSub, "Votre abonnement est réactivé. Merci de continuer l'aventure avec nous !");

  // Cancelled but still running: access is kept until endAt, then no renewal
  const renderCancelled = (source: string) => (
    <>
      <span className="pf-status pf-status--warning">
        Résilié{receipt.canceledAt ? ` le ${receipt.canceledAt}` : ""}
      </span>
      <p className="pf-plan">
        {plan}
        {source}
      </p>
      <KeyDate label="Fin de votre accès" date={receipt.endAt} tone="warning" />
      <p className="pf-detail">
        Vous profitez encore de toutes les séries jusqu'au <strong>{receipt.endAt}</strong>. Votre abonnement ne sera pas
        renouvelé et aucun nouveau prélèvement ne sera effectué. Ensuite, vous pourrez continuer à lire un épisode gratuit
        par jour.
      </p>
      <p className="pf-hint">
        <Icon aria-hidden="true">info_outline</Icon>
        {receipt.receiptType === "stripe"
          ? `Vous changez d'avis ? Réactivez votre abonnement avant le ${receipt.endAt} pour le garder sans interruption.`
          : "Vous changez d'avis ? Réactivez le renouvellement depuis les réglages d'abonnement de l'App Store ou de Google Play."}
      </p>
    </>
  );

  const renderStatus = () => {
    switch (receipt.receiptType) {
      case "stripe":
        if (receipt.cancelAtPeriodEnd) return renderCancelled("");
        return (
          <>
            {receipt.status === "past_due" ? (
              <span className="pf-status pf-status--warning">Paiement en attente</span>
            ) : (
              <span className="pf-status pf-status--active">Actif</span>
            )}
            <p className="pf-plan">{plan}</p>
            <KeyDate label="Prochain renouvellement" date={receipt.endAt} />
            {receipt.status === "past_due" ? (
              <p className="pf-hint pf-hint--warning">
                <Icon aria-hidden="true">warning_amber</Icon>
                Le dernier prélèvement n'a pas abouti. Mettez votre carte à jour pour garder votre accès.
              </p>
            ) : (
              <p className="pf-detail">Votre abonnement se renouvelle automatiquement. Vous pouvez le résilier à tout moment.</p>
            )}
          </>
        );
      case "in-app":
        if (receipt.cancelAtPeriodEnd) return renderCancelled(" · application mobile");
        return (
          <>
            <span className="pf-status pf-status--active">Actif</span>
            <p className="pf-plan">{plan} · application mobile</p>
            <KeyDate label="Prochain renouvellement" date={receipt.endAt} />
            <p className="pf-hint">
              <Icon aria-hidden="true">info_outline</Icon>
              Pour modifier ou résilier cet abonnement, rendez-vous dans les réglages d'abonnement de l'App Store ou de
              Google Play.
            </p>
          </>
        );
      case "gift":
        return (
          <>
            <span className="pf-status pf-status--gift">Code prépayé</span>
            <p className="pf-plan">
              {receipt.duration} {receipt.timeUnit === "m" ? "mois" : "semaines"} d'accès offerts
            </p>
            <p className="pf-detail">
              Votre accès à Nabook se termine le <strong>{receipt.endAt}</strong>.
            </p>
          </>
        );
      case "other":
        return (
          <>
            <span className="pf-status pf-status--active">Actif</span>
            <p className="pf-plan">Accès offert par un partenaire</p>
            <p className="pf-detail">
              Votre accès à toutes les séries vous est offert par un partenaire de Nabook (médiathèque, offre spéciale…).
            </p>
            <p className="pf-hint">
              <Icon aria-hidden="true">info_outline</Icon>
              Une question sur votre accès ? Écrivez-nous à hello@nabook.co.
            </p>
          </>
        );
      case "none":
      default:
        return (
          <>
            <span className="pf-status">Non abonné</span>
            <p className="pf-plan">Profitez d'un épisode gratuit chaque jour</p>
            <p className="pf-detail">
              Abonnez-vous pour lire toutes les séries en illimité, depuis l'application mobile ou en ouvrant un épisode
              réservé aux abonnés.
            </p>
          </>
        );
    }
  };

  const isStripe = receipt.receiptType === "stripe";

  return (
    <section className="nbk-card pf-card pf-subscription" aria-labelledby="pf-subscription-title">
      <h2 id="pf-subscription-title" className="pf-card__title">
        <Icon aria-hidden="true">workspace_premium</Icon>
        Abonnement
      </h2>

      {renderStatus()}

      <div aria-live="polite">
        {feedback ? (
          <p className={`nbk-feedback nbk-feedback--${feedback.type}`} role={feedback.type === "error" ? "alert" : undefined}>
            <Icon aria-hidden="true">{feedback.type === "error" ? "error_outline" : "check_circle"}</Icon>
            <span>{feedback.message}</span>
          </p>
        ) : null}
      </div>

      {isStripe && showCardForm && clientSecret ? (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutForm
            clientSecret={clientSecret}
            onCancel={() => setShowCardForm(false)}
            onSuccess={async () => {
              setShowCardForm(false);
              // A setup intent can only be confirmed once: fetch a new one for the next update
              await onChanged();
              setFeedback({ type: "success", message: "Votre carte a bien été mise à jour." });
            }}
          />
        </Elements>
      ) : null}

      {isStripe && !showCardForm ? (
        <div className="pf-actions">
          {receipt.cancelAtPeriodEnd ? (
            <button type="button" className="nbk-btn nbk-btn--primary" onClick={reactivateSubscription} disabled={isLoading}>
              {isLoading ? <CircularProgress size={18} color="inherit" aria-hidden="true" /> : null}
              Réactiver mon abonnement
            </button>
          ) : null}
          {clientSecret ? (
            <button
              type="button"
              className="nbk-btn nbk-btn--ghost"
              onClick={() => {
                setFeedback(null);
                setShowCardForm(true);
              }}
              disabled={isLoading}
            >
              <Icon aria-hidden="true">credit_card</Icon>
              Mettre à jour ma carte
            </button>
          ) : null}
          {!receipt.cancelAtPeriodEnd ? (
            <button
              type="button"
              className="pf-link-btn pf-link-btn--danger"
              onClick={() => {
                setFeedback(null);
                setIsConfirmingCancel(true);
              }}
              disabled={isLoading}
            >
              Résilier l'abonnement
            </button>
          ) : null}
        </div>
      ) : null}

      <Dialog
        open={isConfirmingCancel}
        onClose={() => !isLoading && setIsConfirmingCancel(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="pf-cancel-title"
        aria-describedby="pf-cancel-text"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <div className="nbk-dialog">
          <div className="nbk-dialog__icon nbk-dialog__icon--warning" aria-hidden="true">
            <Icon>heart_broken</Icon>
          </div>
          <h2 id="pf-cancel-title" className="nbk-dialog__title">
            Résilier votre abonnement ?
          </h2>
          <p id="pf-cancel-text" className="nbk-dialog__text">
            Vous garderez l'accès à toutes les séries jusqu'au <strong>{receipt.endAt}</strong>. Vous pourrez réactiver
            votre abonnement à tout moment d'ici là.
          </p>
          <div className="nbk-dialog__actions">
            <button
              type="button"
              className="nbk-btn nbk-btn--primary"
              onClick={() => setIsConfirmingCancel(false)}
              disabled={isLoading}
              autoFocus
            >
              Garder mon abonnement
            </button>
            <button type="button" className="nbk-btn nbk-btn--danger" onClick={cancelSubscription} disabled={isLoading}>
              {isLoading ? <CircularProgress size={18} color="inherit" aria-hidden="true" /> : null}
              Confirmer la résiliation
            </button>
          </div>
        </div>
      </Dialog>
    </section>
  );
};

export default SubscriptionCard;
